import { useEffect, useState } from "react";

const roundToTwoDecimals = (num: number): number =>
    Math.round(num * 100) / 100;

export interface Totals {
    /** Parent service items only */
    subTotal: number;
    discount: number;
    netTotal: number;
    gst: number;
    grandTotal: number;
    cgst?: number;
    sgst?: number;
    igst?: number;
    /** Accessories aggregated across all service items */
    accessorySubTotal: number;
    accessoryDiscount: number;
    accessoryNetTotal: number;
    accessoryGst: number;
    accessoryGrandTotal: number;
    /** Parent + accessories */
    combinedSubTotal: number;
    combinedDiscount: number;
    combinedNetTotal: number;
    combinedGst: number;
    combinedGrandTotal: number;
    currency_symbol: string;
}

const emptyTotals = (currencySymbol: string): Totals => ({
    subTotal: 0,
    discount: 0,
    netTotal: 0,
    gst: 0,
    grandTotal: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    accessorySubTotal: 0,
    accessoryDiscount: 0,
    accessoryNetTotal: 0,
    accessoryGst: 0,
    accessoryGrandTotal: 0,
    combinedSubTotal: 0,
    combinedDiscount: 0,
    combinedNetTotal: 0,
    combinedGst: 0,
    combinedGrandTotal: 0,
    currency_symbol: currencySymbol,
});

export const useQuotationCalculations = (
    items: any[],
    formType: "enquiry" | "quotation" | "sales_order" | "purchase_request",
    billFromAddress?: any,
    shippingAddresses?: { id: string; address: string; state?: string; city?: string; pin_code?: string }[],
    formik?: any
) => {
    const currencySymbol =
        (formik?.values?.currency_code === "INR" || formik?.values?.currency_code === "IND") ? "₹" : "@";

    const [totals, setTotals] = useState<Totals>(emptyTotals(currencySymbol));

    useEffect(() => {
        if (!items || items.length === 0) {
            setTotals(emptyTotals(currencySymbol));
            return;
        }

        const isINR = formik?.values?.currency_code === "INR" || formik?.values?.currency_code === "IND";

        let subTotal = 0;
        let discount = 0;
        let netTotal = 0;
        let gst = 0;

        let accessorySubTotal = 0;
        let accessoryDiscount = 0;
        let accessoryNetTotal = 0;
        let accessoryGst = 0;
        let accessoryGrandTotal = 0;

        items.forEach((item) => {
            const qty = Number(item.quantity || item.productQuantity) || 0;

            let total = 0;
            if (item.gross_value && Number(item.gross_value) > 0) {
                total = Number(item.gross_value);
            } else {
                let rate = 0;
                if (!isINR && item.rate_in_currency && Number(item.rate_in_currency) > 0) {
                    rate = Number(item.rate_in_currency);
                } else if (item.rate_in && Number(item.rate_in) > 0) {
                    rate = Number(item.rate_in);
                } else if (item.serviceRate && Number(item.serviceRate) > 0) {
                    rate = Number(item.serviceRate);
                } else {
                    rate = Number(item.unit_price) || 0;
                }
                total = qty * rate;
            }

            const discountAmount = Number(item.discount_amount) || Number(item.discount_value) || 0;

            let netVal = 0;
            if (item.net_value && Number(item.net_value) > 0) {
                netVal = Number(item.net_value);
            } else {
                netVal = total - discountAmount;
            }

            const gstVal = isINR ? Number(item.gst_value) || Number(item.original_gst_value) || 0 : 0;

            subTotal += total;
            discount += discountAmount;
            netTotal += netVal;
            gst += isINR ? gstVal : 0;

            const accessories = Array.isArray(item.accessories) ? item.accessories : [];
            accessories.forEach((acc: any) => {
                const accQty = Number(acc.quantity) || 0;
                const accRate = Number(acc.unit_price) || 0;
                const accGross = Number(acc.gross_value) || accQty * accRate;
                const accDisc = Number(acc.discount_amount) || 0;
                const accTaxable =
                    Number(acc.taxable_amount) ||
                    Math.max(accGross - accDisc, 0);
                const accGstVal = isINR ? Number(acc.gst_value) || 0 : 0;
                const accLineTotal =
                    Number(acc.total_amount) ||
                    accTaxable + accGstVal;

                accessorySubTotal += accGross;
                accessoryDiscount += accDisc;
                accessoryNetTotal += accTaxable;
                accessoryGst += accGstVal;
                accessoryGrandTotal += accLineTotal;
            });
        });

        const roundedSub = Number.isFinite(subTotal) ? roundToTwoDecimals(subTotal) : 0;
        const roundedDis = Number.isFinite(discount) ? roundToTwoDecimals(discount) : 0;
        const roundedNet = Number.isFinite(netTotal) ? roundToTwoDecimals(netTotal) : 0;
        const roundedGST = isINR && Number.isFinite(gst) ? roundToTwoDecimals(gst) : 0;

        const normalizeState = (s: any) => (s || "").toString().trim().toLowerCase();
        const billFromState = normalizeState(billFromAddress?.org_state || billFromAddress?.state);
        const billToState = normalizeState(
            formik?.values?.billing_address?.state ||
            formik?.values?.bill_to_address?.state ||
            formik?.values?.bill_to?.state
        );
        const isIntraState = !!billFromState && !!billToState && billFromState === billToState;

        let roundedCGST = 0;
        let roundedSGST = 0;
        let roundedIGST = 0;

        if (formType === "sales_order" && isINR) {
            if (isIntraState) {
                roundedCGST = roundToTwoDecimals(roundedGST / 2);
                roundedSGST = roundToTwoDecimals(roundedGST / 2);
                roundedIGST = 0;
            } else {
                roundedCGST = 0;
                roundedSGST = 0;
                roundedIGST = roundedGST;
            }
        }

        const grandTotalCalc = isINR ? (roundedNet + roundedGST) : roundedNet;
        const finalGrandTotal = (Number.isFinite(grandTotalCalc) && !isNaN(grandTotalCalc) && grandTotalCalc >= roundedNet)
            ? grandTotalCalc
            : (Number.isFinite(roundedNet) && !isNaN(roundedNet) ? roundedNet : 0);

        const roundedAccSub = roundToTwoDecimals(accessorySubTotal);
        const roundedAccDis = roundToTwoDecimals(accessoryDiscount);
        const roundedAccNet = roundToTwoDecimals(accessoryNetTotal);
        const roundedAccGst = roundToTwoDecimals(accessoryGst);
        const roundedAccGrand = roundToTwoDecimals(accessoryGrandTotal);

        const combinedSub = roundToTwoDecimals(roundedSub + roundedAccSub);
        const combinedDis = roundToTwoDecimals(roundedDis + roundedAccDis);
        const combinedNet = roundToTwoDecimals(roundedNet + roundedAccNet);
        const combinedGst = roundToTwoDecimals(roundedGST + roundedAccGst);
        const combinedGrand = roundToTwoDecimals(finalGrandTotal + roundedAccGrand);

        setTotals({
            subTotal: roundedSub,
            discount: roundedDis,
            netTotal: roundedNet,
            gst: roundedGST,
            cgst: roundedCGST,
            sgst: roundedSGST,
            igst: roundedIGST,
            grandTotal: roundToTwoDecimals(finalGrandTotal),
            accessorySubTotal: roundedAccSub,
            accessoryDiscount: roundedAccDis,
            accessoryNetTotal: roundedAccNet,
            accessoryGst: roundedAccGst,
            accessoryGrandTotal: roundedAccGrand,
            combinedSubTotal: combinedSub,
            combinedDiscount: combinedDis,
            combinedNetTotal: combinedNet,
            combinedGst: combinedGst,
            combinedGrandTotal: combinedGrand,
            currency_symbol: currencySymbol,
        });
    }, [
        items,
        formType,
        billFromAddress,
        shippingAddresses,
        formik?.values?.currency_code,
        formik?.values?.billing_address?.state,
        formik?.values?.bill_to_address?.state,
        // Recompute when accessory arrays change
        JSON.stringify(
            (items || []).map((i: any) =>
                (i.accessories || []).map((a: any) => [
                    a.product_id,
                    a.quantity,
                    a.unit_price,
                    a.discount_value,
                    a.discount_type,
                    a.gst_percentage,
                    a.total_amount,
                ])
            )
        ),
    ]);

    return totals;
};
